import { useMutation, useQuery } from '@apollo/client';
import useReactRouter from "use-react-router";
import { faBoxOpen, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import { Alert, Breadcrumb, Button, ButtonGroup, Form, Spinner, Table } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import * as XLSX from 'xlsx/xlsx.mjs';
import { BRAND_PURPLE, BRAND_RED, COLOR_APP, COLOR_TEXT, COLOR_WHITE, VANESSA_SHOP_ID } from '../../../constants';
import { GET_OPTION_VALUES } from '../../../graphql/optionValue/query';
import { GET_SUB_CATEGORIES } from '../../../graphql/subCategory';
import { IMPORT_STOCKS_FROM_EXCEL } from '../../../graphql/stock/mutation';
import { GET_CATEGORYS } from '../../../graphql/category/query';
import { GET_BRANDS } from '../../../graphql/brand/query';
import { GET_UNITS } from '../../../graphql/unit/query';
import BasicModal from '../../../components/BasicModal';
import Illustration from "../../../image/Illustration.png";
import { useToasts } from 'react-toast-notifications';
import DownloadExcelButton from './DownloadExcelButton';
import useWindowDimensions from '../../../helpers/useWindowDimensions';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
    buildOptionColumns,
    CURRENCIES,
    describeImportResult,
    findDuplicateCodes,
    parseSheetRows,
    toImportInput,
    toNumber,
    validateRow,
} from './productExcelImport';


const normalizeName = (value) => (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
//* ພື້ນຫຼັງແຖວທີ່ມີບັນຫາ = ສີແບຣນແບບຈາງ (ຂອງ BRAND_RED)
const PROBLEM_ROW_BACKGROUND = '#FDF0F2';

function ProductExcelAdd() {
    const { history } = useReactRouter();
    const { addToast } = useToasts();
    const { width } = useWindowDimensions();
    const shopId = localStorage.getItem("SHOP");

    const [stockDatas, setStockDatas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedStock, setSelectedStock] = useState();
    const [showDeleteLiveConfirm, setShowDeleteLiveConfirm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [progress, setProgress] = useState(null);
    const [savingRowId, setSavingRowId] = useState(null);
    const [ignoredColumns, setIgnoredColumns] = useState([]);

    //* ຄໍລຳ option ເປີດສະເພາະຮ້ານ Vanessa ກ່ອນ
    const canUseOptions = VANESSA_SHOP_ID.includes(shopId);
    const { data: optionData, loading: optionLoading, error: optionError } = useQuery(GET_OPTION_VALUES, {
        variables: { where: { shop: shopId, isDeleted: false }, orderBy: "createdAt_ASC", limit: 5000 },
        skip: !canUseOptions,
        fetchPolicy: "network-only",
    });
    const optionColumns = useMemo(
        () => (canUseOptions ? buildOptionColumns(optionData?.optionValues?.data) : []),
        [canUseOptions, optionData],
    );
    //* ໃຊ້ສະແດງປ້າຍ "ໃໝ່" ຂອງປະເພດຍ່ອຍເທົ່ານັ້ນ — backend ເປັນຜູ້ຕັດສິນຈິງ
    const { data: subCategoryData } = useQuery(GET_SUB_CATEGORIES, {
        variables: { limit: 1000 },
        skip: !canUseOptions,
        fetchPolicy: "cache-and-network",
    });
    //* ໂຫຼດ option ບໍ່ສຳເລັດ ຫ້າມເປີດໃຫ້ອ່ານໄຟລ໌ ບໍ່ດັ່ງນັ້ນຄໍລຳ option ຈະຖືກຂ້າມທັງໝົດ
    const optionsBlocked = canUseOptions && (optionLoading || !!optionError);

    const [importStocks] = useMutation(IMPORT_STOCKS_FROM_EXCEL);

    //* ໃຊ້ສະແດງປ້າຍ "ໃໝ່" ເທົ່ານັ້ນ — backend ເປັນຜູ້ຕັດສິນຈິງວ່າຈະໃຊ້ຂອງເດີມ ຫຼື ສ້າງໃໝ່
    const shopWhere = { shop: shopId, isDeleted: false };
    const { data: categoryData } = useQuery(GET_CATEGORYS, { variables: { where: shopWhere, limit: 1000 }, skip: !shopId, fetchPolicy: "cache-and-network" });
    const { data: brandData } = useQuery(GET_BRANDS, { variables: { where: shopWhere, limit: 1000 }, skip: !shopId, fetchPolicy: "cache-and-network" });
    const { data: unitData } = useQuery(GET_UNITS, { variables: { where: shopWhere }, skip: !shopId, fetchPolicy: "cache-and-network" });

    const categoryByName = useMemo(() => new Map((categoryData?.categories?.data ?? []).map((c) => [normalizeName(c?.name), c])), [categoryData]);
    const unitNames = useMemo(() => new Set((unitData?.units?.data ?? []).map((u) => normalizeName(u?.name))), [unitData]);
    const isNewCategory = (name) => !!normalizeName(name) && !categoryByName.has(normalizeName(name));
    const isNewSubCategory = (name, categoryName) => {
        if (!normalizeName(name) || !normalizeName(categoryName)) return false;
        const categoryId = categoryByName.get(normalizeName(categoryName))?.id;
        return !(subCategoryData?.subCategories?.data ?? []).some((s) =>
            normalizeName(s?.name) === normalizeName(name) && s?.category?.id === categoryId);
    };
    const isNewUnit = (name) => !!normalizeName(name) && !unitNames.has(normalizeName(name));
    const isNewBrand = (name, categoryName) => {
        if (!normalizeName(name)) return false;
        const categoryId = categoryByName.get(normalizeName(categoryName))?.id;
        return !(brandData?.brands?.data ?? []).some((b) =>
            normalizeName(b?.name) === normalizeName(name) && (!b?.category?.id || b?.category?.id === categoryId));
    };

    const duplicateCodes = useMemo(() => findDuplicateCodes(stockDatas), [stockDatas]);
    const rowsWithErrors = useMemo(() => stockDatas.filter((stock) => validateRow(stock, duplicateCodes, optionColumns).length > 0).length, [stockDatas, duplicateCodes, optionColumns]);
    const imageFailedRows = useMemo(() => stockDatas.filter((stock) => stock.importStatus === "IMAGE_FAILED").length, [stockDatas]);
    const totalImportAmount = useMemo(() => stockDatas.reduce((sum, stock) => {
        const amount = toNumber(stock?.amount);
        return sum + (amount > 0 ? amount : 0);
    }, 0), [stockDatas]);

    const updateStock = (id, patch) => {
        setStockDatas((rows) => rows.map((row) => (row.id === id
            //* ແກ້ແຖວແລ້ວ ລ້າງຜົນເກົ່າ ເພື່ອບໍ່ໃຫ້ຂໍ້ຄວາມຜິດພາດຄ້າງ
            ? { ...row, ...patch, importStatus: undefined, importError: undefined }
            : row)));
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        event.target.value = "";
        if (!file) return;
        setError("");
        if (!isValidFileType(file)) {
            setError("ຮອງຮັບສະເພາະໄຟລ໌ .xlsx, .xlsm, .xlsb, .xltx ແລະ .xls");
            return;
        }
        setIsLoading(true);
        const reader = new FileReader();
        reader.onerror = () => {
            setError("ອ່ານໄຟລ໌ບໍ່ສຳເລັດ");
            setIsLoading(false);
        };
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(e.target.result, { type: 'binary' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const { error: parseError, rows, ignoredColumns: unknownColumns } = parseSheetRows(
                    XLSX.utils.sheet_to_json(worksheet, { header: 1 }),
                    optionColumns,
                    { allowSubCategory: canUseOptions },
                );
                if (parseError) {
                    setError(parseError);
                    setStockDatas([]);
                    setIgnoredColumns([]);
                } else {
                    setStockDatas(rows);
                    setIgnoredColumns(unknownColumns);
                }
            } catch (readError) {
                console.error("Error reading Excel file:", readError);
                setError("ອ່ານໄຟລ໌ບໍ່ສຳເລັດ");
            }
            setIsLoading(false);
        };
        reader.readAsBinaryString(file);
    };

    const isValidFileType = (file) => {
        return ['xlsx', 'xlsm', 'xlsb', 'xltx', 'xls'].some((ext) => file.name.endsWith(ext));
    };

    const onSave = async (ignoreFailedImages = false) => {
        if (isSaving || stockDatas.length === 0 || rowsWithErrors > 0) return;
        setIsSaving(true);
        let remaining = stockDatas;
        const total = stockDatas.length;
        let done = 0;
        let created = 0;
        setProgress({ done: 0, total, created: 0, failed: 0 });

        //* ສົ່ງເທື່ອລະແຖວ ເພື່ອໃຫ້ໜ້າຈໍອັບເດດທັນທີທີ່ແຕ່ລະລາຍການແລ້ວ
        //* ຫ້າມສົ່ງພ້ອມກັນ: ປະເພດ/ແບຣນ/ປະເພດຍ່ອຍທີ່ແຖວກ່ອນສ້າງ ຕ້ອງເຫັນໃນແຖວຖັດໄປ ບໍ່ດັ່ງນັ້ນຈະສ້າງຊ້ຳ
        for (const row of stockDatas) {
            setSavingRowId(row.id);
            try {
                const { data } = await importStocks({
                    variables: {
                        rows: [toImportInput(row, ignoreFailedImages && row.importStatus === "IMAGE_FAILED", optionColumns)],
                    },
                });
                const result = (data?.importStocksFromExcel?.results ?? []).find((r) => r.rowKey === row.id);
                if (result?.status === "CREATED") {
                    created += 1;
                    remaining = remaining.filter((item) => item.id !== row.id);
                } else {
                    remaining = remaining.map((item) => (item.id === row.id
                        ? { ...item, importStatus: result?.status ?? "ERROR", importError: describeImportResult(result) }
                        : item));
                }
            } catch (saveError) {
                console.log("import excel row error: ", saveError);
                //* ບໍ່ຮູ້ວ່າ server ສ້າງໄປແລ້ວຫຼືບໍ່ (ເຊັ່ນ ເນັດຫຼຸດ) ຈຶ່ງເຕືອນໃຫ້ກວດກ່ອນລອງໃໝ່
                remaining = remaining.map((item) => (item.id === row.id
                    ? { ...item, importStatus: "ERROR", importError: "ບໍ່ໄດ້ຮັບຜົນຈາກ server ກະລຸນາກວດໃນຄັງສິນຄ້າກ່ອນລອງໃໝ່" }
                    : item));
            }
            done += 1;
            setProgress({ done, total, created, failed: done - created });
            setStockDatas(remaining);
        }

        setSavingRowId(null);
        setIsSaving(false);
        setProgress(null);
        if (remaining.length === 0) {
            addToast(`ເພີ່ມສິນຄ້າສຳເລັດ ${created} ລາຍການ`, { appearance: "success", autoDismiss: true });
            history.goBack();
            return;
        }
        addToast(`ສຳເລັດ ${created} ລາຍການ • ຍັງເຫຼືອ ${remaining.length} ລາຍການ ກະລຸນາກວດຂໍ້ຄວາມສີແດງ`, {
            appearance: created > 0 ? "warning" : "error",
            autoDismiss: true,
        });
    };

    const NewBadge = () => (
        <span style={{ background: BRAND_PURPLE, color: COLOR_WHITE, borderRadius: 4, fontSize: 12, padding: '1px 6px', marginTop: 3, display: 'inline-block' }}>ໃໝ່</span>
    );
    const textInput = (stock, field, style) => (
        <Form.Control type="text" value={stock[field] ?? ""} style={style}
            onChange={(e) => updateStock(stock.id, { [field]: e.target.value })} />
    );
    const numberInput = (stock, field) => (
        <NumberFormat
            name={field}
            onChange={(e) => updateStock(stock.id, { [field]: e.target.value })}
            value={stock[field]}
            className="form-control"
            style={{ minWidth: 100 }}
            placeholder="0"
            thousandSeparator={true}
            prefix={''} />
    );

    return (
        <div style={{ paddingBottom: '5em' }} >
            <div style={{ height: 20 }} />
            <Breadcrumb style={{ marginBottom: 0 }} >
                <Breadcrumb.Item style={{ fontSize: 14, color: COLOR_TEXT }} onClick={() => history.goBack()}>
                    {/* COLOR_APP ເປັນ gradient ໃຊ້ເປັນສີຕົວອັກສອນບໍ່ໄດ້ */}
                    <FontAwesomeIcon icon={faBoxOpen} style={{ color: COLOR_TEXT }} /><span style={{ color: COLOR_TEXT }}>ຄັງສິນຄ້າ</span></Breadcrumb.Item>
                <Breadcrumb.Item style={{ fontSize: 14 }} active>ເພີ່ມສິນຄ້າດ້ວຍ Excel</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ margin: '0px 5px' }}>
                <div style={{ fontWeight: 'bold' }}>ເລືອກ File Excel ຂອງທ່ານ</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <input className='form-control' style={{ width: '70vh' }} type="file" onChange={handleFileUpload} disabled={isSaving || optionsBlocked} />
                    {width > 700 && <DownloadExcelButton />}
                </div>
                {width <= 700 && <DownloadExcelButton />}
            </div>
            {error && (
                <Alert className="mt-2 mb-0" style={{ background: PROBLEM_ROW_BACKGROUND, borderColor: BRAND_RED, color: BRAND_RED }}>{error}</Alert>
            )}
            {optionError && (
                <Alert className="mt-2 mb-0" style={{ background: PROBLEM_ROW_BACKGROUND, borderColor: BRAND_RED, color: BRAND_RED }}>ໂຫຼດ option ຂອງຮ້ານບໍ່ສຳເລັດ ກະລຸນາລີເຟຣຊໜ້ານີ້</Alert>
            )}
            {ignoredColumns.length > 0 && (
                <Alert className="mt-2 mb-0" style={{ background: PROBLEM_ROW_BACKGROUND, borderColor: BRAND_RED, color: BRAND_RED }}>ລະບົບບໍ່ຮູ້ຈັກຄໍລຳເຫຼົ່ານີ້ ຂໍ້ມູນໃນຄໍລຳຈະບໍ່ຖືກບັນທຶກ: {ignoredColumns.join(", ")}</Alert>
            )}
            <div style={{ padding: '10px 0' }}>
                <p style={{ marginBottom: 4 }}><b>ຂໍ້ຄວນປະຕິບັດ:</b> ຂໍ້ມູນທີ່ຈຳເປັນ: "ຊື່ສິນຄ້າ", "ຫົວຫນວ່ຍ", "ຈຳນວນ", "ລາຄາຂາຍ", "ສະກຸນເງິນ". ປະເພດ, ແບຣນ ແລະ ຫົວໜ່ວຍ ທີ່ຍັງບໍ່ມີໃນຮ້ານ ລະບົບຈະສ້າງໃຫ້ໃໝ່.</p>
                <p style={{ marginBottom: 0 }}><b>ລິ້ງຮູບ:</b> ຕ້ອງເປັນລິ້ງທີ່ເປີດແລ້ວເຫັນໄຟລ໌ຮູບໂດຍກົງ (JPG, PNG, WebP ບໍ່ເກີນ 10MB). ຮູບປະກອບໃສ່ໄດ້ບໍ່ເກີນ 9 ລິ້ງ ຂັ້ນດ້ວຍ ",". ລິ້ງແຊຣ໌ຂອງ Google Drive ຫຼື ໜ້າໂພສ Facebook ໃຊ້ບໍ່ໄດ້.</p>
            </div>
            {isLoading && <LoadingSpinner />}
            {(stockDatas?.length > 0 && !isLoading) && (
                //* ລັອກຕາຕະລາງລະຫວ່າງບັນທຶກ ບໍ່ດັ່ງນັ້ນສິ່ງທີ່ແກ້ລະຫວ່າງທາງຈະຖືກຜົນການບັນທຶກທັບ
                <fieldset disabled={isSaving}>
                <Table responsive className="mt-2">
                    <thead>
                        <tr style={{ whiteSpace: 'nowrap' }}>
                            {["#", "ຊື່ສິນຄ້າ", "ລະຫັດ CF", "ປະເພດ", ...(canUseOptions ? ["ປະເພດຍ່ອຍ"] : []), "ແບຣນ", "ຫົວຫນວ່ຍ", "ຈໍານວນ", "ລາຄາຕົ້ນທືນ", "ລາຄາຂາຍ", "ສະກຸນເງິນ", "ຮູບຫນ້າປົກ", "ຮູບປະກອບ", "ລາຍລະອຽດສິນຄ້າ", ...optionColumns.map((column) => column.header), "ຈັດການ"].map((title) => (
                                <th key={title} style={{ borderBottom: 'none' }}>{title}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {stockDatas.map((stock, index) => {
                            const problems = [...validateRow(stock, duplicateCodes, optionColumns), ...(stock.importError ? [stock.importError] : [])];
                            return (
                                <React.Fragment key={stock.id}>
                                    <tr style={{ whiteSpace: 'nowrap', background: problems.length > 0 ? PROBLEM_ROW_BACKGROUND : undefined }}>
                                        <td style={problems.length > 0 ? { borderBottom: 'none' } : undefined}>
                                            {savingRowId === stock.id ? <Spinner size="sm" animation="border" aria-label="saving-row" style={{ color: BRAND_RED }} /> : index + 1}
                                        </td>
                                        <td>{textInput(stock, "name", { minWidth: 180 })}</td>
                                        <td>{textInput(stock, "cfMessage", { minWidth: 100 })}</td>
                                        <td>{textInput(stock, "category", { minWidth: 130 })}{isNewCategory(stock.category) && <NewBadge />}</td>
                                        {canUseOptions && (
                                            <td>{textInput(stock, "subCategory", { minWidth: 130 })}{isNewSubCategory(stock.subCategory, stock.category) && <NewBadge />}</td>
                                        )}
                                        <td>{textInput(stock, "brand", { minWidth: 130 })}{isNewBrand(stock.brand, stock.category) && <NewBadge />}</td>
                                        <td>{textInput(stock, "unit", { minWidth: 90 })}{isNewUnit(stock.unit) && <NewBadge />}</td>
                                        <td>{numberInput(stock, "amount")}</td>
                                        <td>{numberInput(stock, "originPrice")}</td>
                                        <td>{numberInput(stock, "price")}</td>
                                        <td>
                                            <ButtonGroup aria-label="currency">
                                                {CURRENCIES.map((currency, currencyIndex) => (
                                                    <Button
                                                        key={currency}
                                                        style={{
                                                            background: stock?.currency === currency ? COLOR_APP : "#eee",
                                                            color: stock?.currency === currency ? "#ffff" : "#000",
                                                            width: 50,
                                                            marginLeft: currencyIndex > 0 ? 2 : 0,
                                                            borderWidth: 0,
                                                        }}
                                                        onClick={() => updateStock(stock.id, { currency })}
                                                    >
                                                        {currency}
                                                    </Button>
                                                ))}
                                            </ButtonGroup>
                                        </td>
                                        <td>{textInput(stock, "coverImageUrl", { minWidth: 200 })}</td>
                                        <td>
                                            <Form.Control as="textarea" rows={1} value={stock.imageUrls ?? ""} style={{ minWidth: 220 }}
                                                onChange={(e) => updateStock(stock.id, { imageUrls: e.target.value })} />
                                        </td>
                                        <td>
                                            <Form.Control as="textarea" rows={1} value={stock.description ?? ""} style={{ minWidth: 220 }}
                                                onChange={(e) => updateStock(stock.id, { description: e.target.value })} />
                                        </td>
                                        {optionColumns.map((column) => {
                                            const current = stock.options?.[column.key] ?? "";
                                            //* ຄ່າຈາກໄຟລ໌ທີ່ບໍ່ມີໃນຮ້ານ ຍັງສະແດງໃຫ້ເຫັນ ຜູ້ໃຊ້ຈຶ່ງຮູ້ວ່າຕ້ອງແກ້ຫຍັງ
                                            const choices = current && !column.values.includes(current)
                                                ? [current, ...column.values]
                                                : column.values;
                                            return (
                                                <td key={column.key}>
                                                    <Form.Select
                                                        value={current}
                                                        style={{ minWidth: 110 }}
                                                        aria-label={column.header}
                                                        onChange={(e) => updateStock(stock.id, { options: { ...stock.options, [column.key]: e.target.value } })}
                                                    >
                                                        <option value="">-</option>
                                                        {choices.map((value) => <option key={value} value={value}>{value}</option>)}
                                                    </Form.Select>
                                                </td>
                                            );
                                        })}
                                        <td>
                                            <button
                                                className="btn hoverme"
                                                disabled={isSaving}
                                                onClick={() => {
                                                    setShowDeleteLiveConfirm(true);
                                                    setSelectedStock(stock);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTrashAlt} style={{ color: BRAND_RED }} />
                                            </button>
                                        </td>
                                    </tr>
                                    {problems.length > 0 && (
                                        <tr style={{ background: PROBLEM_ROW_BACKGROUND }}>
                                            <td colSpan={14 + (canUseOptions ? 1 : 0) + optionColumns.length} style={{ color: BRAND_RED, fontSize: 13, paddingTop: 0 }}>
                                                {problems.join(" • ")}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
                </fieldset>
            )}

            <div style={{
                position: "fixed", width: '100%', right: 0, bottom: 0, zIndex: 10, display: 'flex', gap: 5, justifyContent: 'end', padding: 10, background: '#ffffff', flexWrap: 'wrap'
            }}>
                <div style={{ marginRight: 'auto', alignSelf: 'center', fontWeight: 'bold' }}>
                    {progress
                        ? `ກຳລັງບັນທຶກ ${progress.done}/${progress.total} ລາຍການ • ສຳເລັດ ${progress.created} • ບໍ່ສຳເລັດ ${progress.failed}`
                        : `${stockDatas.length} ລາຍການ • ຈຳນວນນຳເຂົ້າ: ${totalImportAmount}${rowsWithErrors > 0 ? ` • ຕ້ອງແກ້ໄຂ ${rowsWithErrors} ລາຍການ` : ''}`}
                </div>
                {imageFailedRows > 0 && (
                    <Button disabled={isSaving} style={{ background: 'transparent', borderColor: BRAND_PURPLE, color: BRAND_PURPLE }} onClick={() => onSave(true)}>
                        ບັນທຶກໂດຍບໍ່ໃສ່ຮູບທີ່ໂຫຼດບໍ່ໄດ້ ({imageFailedRows})
                    </Button>
                )}
                <Button variant='outline-secondary' style={{ minWidth: 200 }} disabled={isSaving} onClick={() => { setStockDatas([]); setError(""); setIgnoredColumns([]); }}>ຍົກເລີກ</Button>
                <Button
                    style={{
                        background: COLOR_APP,
                        border: 'none',
                        minWidth: 200
                    }}
                    disabled={isSaving || stockDatas.length === 0 || rowsWithErrors > 0}
                    onClick={() => onSave(false)}>
                    {isSaving ? <Spinner size="sm" animation="border" /> : "ບັນທືກ"}
                </Button>
            </div>

            <BasicModal
                show={showDeleteLiveConfirm}
                onHide={() => setShowDeleteLiveConfirm(false)}
                okay={() => {
                    setStockDatas((rows) => rows.filter((row) => row.id !== selectedStock?.id));
                    setShowDeleteLiveConfirm(false);
                    setSelectedStock();
                    addToast("ລຶບຂໍ້ມູນສໍາເລັດແລ້ວ!", {
                        appearance: "success",
                        autoDismiss: true,
                    });
                }}
                title="ທ່ານຕ້ອງການລຶບຂໍ້ມູນ ຫຼື ບໍ່"
                leftButtonName="ຍົກເລີກ"
                rightButtonName="ຕົກລົງ"
                Illustration={Illustration} />
        </div>
    );
}

export default ProductExcelAdd;
